import { createAsyncThunk } from '@reduxjs/toolkit';
import { iamClient, minerClient } from 'api/clients';
import { setBaseCurrencySuccess } from 'store/config/config.reducer';
import { setMinersCount } from 'store/miner';
import {
  addCustomerSuccess,
  deleteUserSuccess,
  fetchAllCustomersSuccess,
  getCustomerInfoSuccess,
  requestFailure,
  requestStart,
  updateCustomerBalanceSuccess,
  updateCustomerDepositSuccess,
  updateCustomerInfoSuccess
} from './customerSlice';

import moment from 'moment';

// Fetching miners for a specific customer
export const fetchCustomerMinersThunk = createAsyncThunk(
  'miner/fetchCustomerMiners',
  async (id, { dispatch }) => {
    dispatch(requestStart());

    try {
      const { data } = await minerClient.getAccountMiners(id, {
        page: 0,
        size: 20
      });
      return data.content;
    } catch (error) {
      dispatch(requestFailure(error.toString()));
    }
  }
);

// Fetching customers
export const fetchAllCustomersThunk = createAsyncThunk(
  'customer/fetchAllCustomers',
  async (_, { dispatch }) => {
    dispatch(requestStart());

    try {
      const { users, tenant } = await iamClient.getCustomers();

      // GET tenant carries per-customer COUNTS only (deviceCount / activeDeviceCount /
      // deadDevicesCount); the devices themselves are paged via GET tenant/devices. The
      // headline active/inactive totals still sum correctly from the counts.
      const { activeMiners, inactiveMiners } = users.reduce(
        (acc, user) => {
          acc.activeMiners +=
            user.activeDeviceCount ?? user.activeMinerCount ?? 0;
          acc.inactiveMiners +=
            user.deadDevicesCount ?? user.deadMinersCount ?? 0;
          return acc;
        },
        { activeMiners: 0, inactiveMiners: 0 }
      );

      dispatch(setMinersCount({ activeMiners, inactiveMiners }));
      dispatch(setBaseCurrencySuccess(tenant.baseCurrency));
      dispatch(fetchAllCustomersSuccess(users));
      return users;
    } catch (error) {
      dispatch(requestFailure(error.toString()));
    }
  }
);

// Fetching customer by id
export const fetchCustomerByIdThunk = createAsyncThunk(
  'customer/fetchCustomerById',
  async (id, { dispatch }) => {
    dispatch(requestStart());

    try {
      const data = await iamClient.getCustomer(id);
      dispatch(getCustomerInfoSuccess(data));
      return data.content;
    } catch (error) {
      dispatch(requestFailure(error.toString()));
    }
  }
);

// Adding new customer
export const fetchAddNewCustomerThunk = createAsyncThunk(
  'customer/fetchAddNewCustomer',
  async (formData, { dispatch }) => {
    dispatch(requestStart());

    try {
      const data = await iamClient.createCustomer(formData);
      dispatch(addCustomerSuccess(data));
      return data;
    } catch (error) {
      dispatch(requestFailure(error.toString()));
    }
  }
);

//Update customer
export const updateCustomerThunk = createAsyncThunk(
  'customer/updateCustomerThunk',
  async (formData, { dispatch }) => {
    dispatch(requestStart());
    try {
      const res = await iamClient.updateCustomer(formData);
      dispatch(
        updateCustomerInfoSuccess({
          ...formData,
          dateOfBirth: formData.dateOfBirth
            ? moment(formData.dateOfBirth).format('YYYY-MM-DD')
            : null
        })
      );
      return res;
    } catch (error) {
      dispatch(requestFailure(error.toString()));
      throw error;
    }
  }
);

//Update customer deposit
export const updateSellingElecticityPriceThunk = createAsyncThunk(
  'customer/updateSellingElecticityPriceThunk',
  async ({ id, price }, { dispatch }) => {
    dispatch(requestStart());

    try {
      const res = await iamClient.updateCustomerSellingElectricityPrice(id, {
        price
      });
      dispatch(
        updateCustomerInfoSuccess({
          userId: id,
          electricitySellingPrice: price
        })
      );
      return res;
    } catch (error) {
      dispatch(requestFailure(error.toString()));
      throw error;
    }
  }
);

//Update customer company ID
export const updateCompanyIdThunk = createAsyncThunk(
  'customer/updateCompanyIdThunk',
  async ({ id, companyId }, { dispatch }) => {
    dispatch(requestStart());

    try {
      const res = await iamClient.updateCustomerCompanyId(id, {
        CompanyId: companyId
      });
      dispatch(
        updateCustomerInfoSuccess({
          userId: id,
          companyId
        })
      );
      return res;
    } catch (error) {
      dispatch(requestFailure(error.toString()));
      throw error;
    }
  }
);

//Update customer vat data
export const updateVatPercentageThunk = createAsyncThunk(
  'customer/updateVatDataThunk',
  async ({ id, vatPercentage }, { dispatch }) => {
    dispatch(requestStart());

    try {
      const res = await iamClient.updateCustomerVatPercentage(id, {
        vatPercentage: Number(vatPercentage)
      });
      dispatch(
        updateCustomerInfoSuccess({
          userId: id,
          vatPercentage
        })
      );
      return res;
    } catch (error) {
      dispatch(requestFailure(error.toString()));
      throw error;
    }
  }
);

//Update customer vat data
export const updateVatNumberThunk = createAsyncThunk(
  'customer/updateVatDataThunk',
  async ({ id, vatNumber }, { dispatch }) => {
    dispatch(requestStart());

    try {
      const res = await iamClient.updateCustomerVatNumber(id, {
        vatNumber
      });
      dispatch(
        updateCustomerInfoSuccess({
          userId: id,
          vatNumber
        })
      );
      return res;
    } catch (error) {
      dispatch(requestFailure(error.toString()));
      throw error;
    }
  }
);

//Update customer balance
export const updateCustomerBalanceThunk = createAsyncThunk(
  'customer/updateCustomerBalanceThunk',
  async ({ id, formData }, { dispatch }) => {
    dispatch(requestStart());
    try {
      const res = await iamClient.updateCustomerBalance(id, formData);

      dispatch(
        updateCustomerBalanceSuccess({ userId: id, balance: res.newBalance })
      );

      return res;
    } catch (error) {
      dispatch(requestFailure(error.toString()));
      throw error;
    }
  }
);

//Update customer deposit
export const updateCustomerDepositThunk = createAsyncThunk(
  'customer/updateCustomerDepositThunk',
  async ({ id, amount }, { dispatch }) => {
    dispatch(requestStart());

    try {
      const res = await iamClient.updateCustomerDeposit(id, { amount });
      dispatch(updateCustomerDepositSuccess({ id, amount }));
      return res;
    } catch (error) {
      dispatch(requestFailure(error.toString()));
      throw error;
    }
  }
);

//Update customer note
export const updateCustomerNoteThunk = createAsyncThunk(
  'customer/updateCustomerNoteThunk',
  async ({ id, note }, { dispatch }) => {
    dispatch(requestStart());

    try {
      const res = await iamClient.updateCustomerNote(id, { note });
      dispatch(updateCustomerInfoSuccess({ userId: id, note }));
      return res;
    } catch (error) {
      dispatch(requestFailure(error.toString()));
      throw error;
    }
  }
);

// Delete user thunk
export const deleteUserThunk = createAsyncThunk(
  'users/deleteUserThunk',
  async ({ id }, { dispatch, rejectWithValue }) => {
    dispatch(requestStart());

    try {
      const res = await iamClient.deleteUser(id);

      if (res.success) {
        dispatch(deleteUserSuccess(id));
      }
      return { id };
    } catch (error) {
      dispatch(requestFailure(error.toString()));
      return rejectWithValue(error.toString());
    }
  }
);
